import { useQuery, useSubscription } from '@apollo/client';
import add from 'date-fns/add';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useShowAllAppointmentsToggle } from '@eluve/blocks';
import { convertUrlStringDate, urlSafeDate } from '@eluve/date-utils';
import { AppointmentsBoolExp } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import { useUserIdFromSession } from '@eluve/session-helpers';

import { appointmentTimelineInfoFragment } from './components/AppointmentAgendaTimelineItem';

const getAppointmentRangeSummary = graphql(
  `
    query getAppointmentRangeSummary($where: AppointmentsBoolExp!) {
      appointments(where: $where) {
        ...AppointmentInfo
      }
    }
  `,
  [appointmentTimelineInfoFragment],
);

const appointmentsStreamingSubscription = graphql(
  `
    subscription appointmentStream(
      $initialValue: timestamptz
      $where: AppointmentsBoolExp!
    ) {
      appointmentsStream(
        where: $where
        batchSize: 1
        cursor: { initialValue: { updatedAt: $initialValue }, ordering: ASC }
      ) {
        __typename
        id
      }
    }
  `,
  [appointmentTimelineInfoFragment],
);

export const useCalendarAppointmentData = () => {
  const userId = useUserIdFromSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const dateSearchParam = searchParams.get('date') ?? urlSafeDate();
  // Falls back to today when the URL's ?date= param is missing or malformed
  // (e.g. trailing garbage from a copy/paste). Calling toISOString on an
  // Invalid Date below would otherwise crash the page via the React error
  // boundary.
  const date = convertUrlStringDate(dateSearchParam) ?? new Date();
  const [showAllAppointments] = useShowAllAppointmentsToggle();
  const [streamingDateStart] = useState(new Date().toISOString());

  const setDate = (date: Date | undefined) => {
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.set('date', urlSafeDate(date ?? new Date()));
    setSearchParams(urlSearchParams);
  };

  const range = [date.toISOString(), add(date, { days: 1 }).toISOString()];

  const min = range[0]!;
  const max = range[1]!;

  const baseFilters: AppointmentsBoolExp[] = [
    { startDate: { _gte: min } },
    { startDate: { _lt: max } },
  ];

  // If we're not showing all appointments, we only want to show the appointments for the current user
  if (!showAllAppointments) {
    baseFilters.push({ userId: { _eq: userId } });
  }

  const queryFilters: AppointmentsBoolExp[] = [
    ...baseFilters,
    { status: { _neq: 'CANCELLED' } },
  ];
  const { data, error, loading, refetch } = useQuery(
    getAppointmentRangeSummary,
    {
      skip: !dateSearchParam,
      variables: {
        where: {
          _and: queryFilters,
        },
      },
    },
  );

  // Keep the cache up to date with the latest appointment data
  useSubscription(appointmentsStreamingSubscription, {
    skip: !dateSearchParam,
    variables: {
      initialValue: streamingDateStart,
      where: { _and: baseFilters },
    },
    onData: (sub) => {
      const appointments = sub.data?.data?.appointmentsStream;
      if (appointments && appointments.length > 0) {
        // If we received an event for a newly updated appointment, we need to refetch the data.
        // This allows the subscription to listen to a much smaller/simpler dataset that doesn't
        // cost as much to stream due to permission checks
        refetch();
      }
    },
  });

  return {
    setDate,
    data,
    loading,
    error,
    dateSearchParam,
    date,
  };
};
